/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp.  2017, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/View',
	'../../lib/@waca/core-client/js/core-client/utils/BrowserUtils',
	'underscore'
], function(View, BrowserUtils, _) {


	class ActiveFilterIndicatorView extends View {

		get tagName() {
			return 'div';
		}
		get className() {
			return 'filterAppliedIndicator';
		}

		/**
		 * Constructor
		 *
		 * @param {object} options
		 * @param {object} options.scopes - an object containing the various scopes to be queried
		 * @param {object} options.pageContextAPI - api to query page context about filters
		 * @param {object} options.featureChecker - feature checker instance
		 * @param {object} options.animate - flag to enable/disable animation upon rendering the indicator
		 *
		 */
		constructor({ scopes, pageContextAPI, featureChecker, animate }) {
			super(arguments);

			this.scopesObject = scopes;
			this.pageContextAPI = pageContextAPI;
			this.featureChecker = featureChecker;
			this._animate = true;
			if (animate !== undefined) {
				this._animate = animate;
			}
			if (BrowserUtils.isIE() && !BrowserUtils.isIEEdge()) {
				this.$el.addClass('ie');
			}
		}

		/**
		 * Method to show/hide the filter count badge
		 * The indicator will only be shown if the count is greater than zero
		 * and overrides a 'true' parameter
		 *
		 * @param {boolean} display - TRUE to show and FALSE to hide
		 */
		show(display) {
			if(display && this.getActiveCount() > 0) {

				this.setCount(this.getActiveCount());

				if(this.$el.parent().is(':visible') && this._animate) {
					this.$el.fadeIn(); //fadeIn doesn't work if not visible
				} else {
					this.$el.show();
				}
			} else {
				if(this.$el.parent().is(':visible') && this._animate) {
					this.$el.fadeOut(); //fadeOut doesn't work if not visible
				} else {
					this.$el.hide();
				}
			}
		}

		/**
		 * Determine whether the data filter is a modeled filter
		 * @param {DataFilterAPI} dataFilter DataFilterAPI instance
		 * @returns true if the given filter is a modeled filter otherwise false
		 */
		_isModeledFilter(dataFilter) {
			const metadataColumnList = dataFilter.getMetadataColumnList() || [];
			return metadataColumnList.filter(metadataColumn => metadataColumn.isFilter()).length > 0;
		}

		/**
		 * @returns the combined total of active filters across the global and current tab scopes
		 */
		getActiveCount() {
			let numFilters = 0;
			const scopes = _.values(this.scopesObject);
			scopes.forEach(content => {
				if (this.featureChecker && this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
					numFilters += _.filter(content.getFeature('DataFilters').getFilterList(), filter => {
						return filter.getValueList().length > 0 || this._isModeledFilter(filter);
					}).length;
				} else {
					const scope = content.getContainer() ? content.getId() : 'global';
					numFilters += _.filter(this.pageContextAPI.getPageContextItems({ scope: scope, origin: 'filter' }), item => {
						return item.getValueCount() > 0 || (item.getIsModeledFilter && item.getIsModeledFilter());
					}).length;
				}
			});
			return numFilters;
		}

		/**
		 * Sets the filter indicator to the given count value
		 * If count of zero, immediately hides the indicator
		 * If over 99 active filters are present, instead displays '99+', and css is adjusted accordingly
		 * @param {int} count - the number of active filters to be displayed
		 */
		setCount(count) {
			if(count === 0) {
				if (this._animate) {
					this.$el.fadeOut('default', function() { //default isn't a correct string, will default to 400 milliseconds
						this.$el.text(count);
					}.bind(this));
				} else {
					this.$el.hide();
					this.$el.text(count);
				}
				return;
			}
			if(count > 99) {
				count = '99+';
			}
			this.$el.text(count);
		}

		/**
		 * Sets the scope key to the specified value
		 *
		 * @param {string} key - the key for the scope. Currently either 'globalScope' or 'tabScope'
		 * @param {string} content - ContentAPI of the scope being set
		 */
		setScope(key, content) {
			this.scopesObject[key] = content;
		}

		/**
		 * Returns the DOM node for use
		 */
		getNode() {
			return this.$el;
		}

		remove () {
			ActiveFilterIndicatorView.inherited('remove', this, arguments);
			this.scopesObject = null;
			this.pageContextAPI = null;
		}
	}
	return ActiveFilterIndicatorView;
});
